/* oxlint-disable simple-import-sort/imports -- prismjs core must load before its language components */
import Prism from 'prismjs';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-yaml';
/* oxlint-enable simple-import-sort/imports */

import {CopyButton, Icon} from '@wandb/components';
import React, {useLayoutEffect, useRef, useState} from 'react';

import * as S from './CodeSnippet.styles';
import {COLLAPSED_CODE_HEIGHT} from './CodeSnippet.styles';

type CodeSnippetProps = {
  showExpandCollapse?: boolean;
  codeSnippet: string;
  analyticsEvent?: (data: any) => void;
  analyticsDefaultProperties?: Record<string, any>;
  backgroundColor?: string;
  codeLanguage?: string;
  fontFamily?: string;
  showBorder?: boolean;
};

export const CodeSnippet = ({
  showExpandCollapse,
  codeSnippet,
  analyticsEvent,
  analyticsDefaultProperties,
  backgroundColor,
  codeLanguage = 'python',
  fontFamily,
  showBorder,
}: CodeSnippetProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const codeRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  // Use ResizeObserver to detect overflow changes
  // to handle transitions/animations in WBAgent disclosures
  // where the element has no dimensions on mount.
  const codeContainerRef = (node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node != null && showExpandCollapse) {
      observerRef.current = new ResizeObserver(() => {
        // When expanded, scrollHeight === clientHeight,
        // so compare against the collapsed threshold directly instead.
        const {scrollHeight, clientHeight} = node;
        setHasOverflow(
          scrollHeight > Math.min(clientHeight, COLLAPSED_CODE_HEIGHT)
        );
      });
      observerRef.current.observe(node);
    }
  };

  const trackCodeCopyAnalytics = () => {
    analyticsEvent?.({
      ...analyticsDefaultProperties,
      codeSnippet,
    });
  };

  useLayoutEffect(() => {
    if (codeRef.current != null) {
      Prism.highlightElement(codeRef.current);
    }
  }, [codeSnippet]);

  return (
    <S.CodeSnippetContainer
      ref={containerRef}
      $isExpanded={isExpanded || !showExpandCollapse}
      $backgroundColor={backgroundColor}
      $showBorder={showBorder}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}>
      {isHovering && (
        <S.CodeCopyButton>
          <CopyButton
            aria-label={'Copy code'}
            content={codeSnippet}
            onCopy={trackCodeCopyAnalytics}
          />
        </S.CodeCopyButton>
      )}
      <S.CodeSnippet
        ref={codeContainerRef}
        $isExpanded={isExpanded || !showExpandCollapse}>
        <S.CopyButtonSpacer />
        <S.PrismCodeHighlighting
          ref={codeRef}
          $fontFamily={fontFamily}
          className={`language-${codeLanguage}`}>
          {codeSnippet}
        </S.PrismCodeHighlighting>
      </S.CodeSnippet>
      {showExpandCollapse && hasOverflow && (
        <S.CodeSnippetExpandCollapse
          onClick={() => {
            if (isExpanded) {
              setIsExpanded(false);
              // Keeps the code snippet in view on collapse
              requestAnimationFrame(() => {
                containerRef.current?.scrollIntoView({
                  block: 'nearest',
                  behavior: 'smooth',
                });
              });
            } else {
              setIsExpanded(true);
            }
          }}>
          <S.IconContainer>
            {isExpanded ? 'Show less' : 'Show more'}
            <Icon
              aria-label={isExpanded ? 'Show less' : 'Show more'}
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
            />
          </S.IconContainer>
        </S.CodeSnippetExpandCollapse>
      )}
    </S.CodeSnippetContainer>
  );
};
